import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import {
	buildFetchTimeoutError,
	FETCH_TIMEOUT_MS_DEFAULT,
	FETCH_TIMEOUT_MS_UPLOAD,
} from "@Front/Utils/networkTimeoutHelpers";

export type FetchTimeoutProfile = "default" | "upload";

const TIMEOUT_BY_PROFILE: Record<FetchTimeoutProfile, number> = {
	default: FETCH_TIMEOUT_MS_DEFAULT,
	upload: FETCH_TIMEOUT_MS_UPLOAD,
};

export type FetchWithTimeoutOptions = {
	timeoutMs?: number;
	timeoutProfile?: FetchTimeoutProfile;
};

function mergeAbortSignals(signals: AbortSignal[]): AbortSignal {
	const controller = new AbortController();
	for (const signal of signals) {
		if (signal.aborted) {
			controller.abort(signal.reason);
			return controller.signal;
		}
		signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
	}
	return controller.signal;
}

export function resolveFetchTimeoutMs(options?: FetchWithTimeoutOptions): number {
	if (typeof options?.timeoutMs === "number" && options.timeoutMs > 0) {
		return options.timeoutMs;
	}
	const profile = options?.timeoutProfile ?? "default";
	return TIMEOUT_BY_PROFILE[profile];
}

/**
 * Returns merged abort signal and cleanup for explicit fetch timeout.
 */
export function createFetchTimeoutAbort(
	init: RequestInit | undefined,
	options: FetchWithTimeoutOptions | undefined,
	method: string,
	urlString: string,
): { signal: AbortSignal; cleanup: () => void } {
	const timeoutMs = resolveFetchTimeoutMs(options);
	const timeoutController = new AbortController();
	const timeoutId = setTimeout(() => {
		LoggerService.warn("⚠️ [HttpClient] Fetch timeout", { method, url: urlString, timeoutMs });
		timeoutController.abort(buildFetchTimeoutError(method, urlString));
	}, timeoutMs);

	const existingSignal = init?.signal;
	const signal = existingSignal ? mergeAbortSignals([existingSignal, timeoutController.signal]) : timeoutController.signal;

	return {
		signal,
		cleanup: (): void => {
			clearTimeout(timeoutId);
		},
	};
}
